import { useState, useEffect } from 'react';
import { Box, Typography, Modal } from '@mui/material';
import {
  CheckCircleOutline,
  CancelOutlined,
  PlayArrow,
  CloseRounded,
  ErrorOutline,
} from '@mui/icons-material';

import { FCheckbox, FHButton, FHTableContainer, FHTooltip } from '../../../../../base';
import { isValidStageTransition } from '../utils/general';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY2,
  FORTA_GRAY4,
  FORTA_ORANGE,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../../../../../utils/colors';

import type { ITableHeaderConfig, ITreatmentPlanGoalTarget } from '../../../../../../utils/types';
import type { TargetStages, GoalTypes } from '../../common/constants/general';

interface StageChangeConfirmationProps {
  open: boolean;
  targets: ITreatmentPlanGoalTarget[];
  selectedStage: TargetStages;
  selectedDate: string;
  goalType: GoalTypes;
  onClose: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, reason?: string) => void;
  onApply: (selectedTargets: ITreatmentPlanGoalTarget[]) => void;
}

interface StageValidationResult {
  isValid: boolean;
  reason?: string;
}

function StageChangeConfirmation({
  open,
  targets,
  selectedStage,
  selectedDate,
  goalType,
  onClose,
  onApply,
}: StageChangeConfirmationProps) {
  const [selectedTargets, setSelectedTargets] = useState<ITreatmentPlanGoalTarget[]>([]);
  const [validationResults, setValidationResults] = useState<{
    [key: string]: StageValidationResult;
  }>({});

  useEffect(() => {
    if (!open) {
      setSelectedTargets([]);
      setValidationResults({});
    }
  }, [open]);

  useEffect(() => {
    // Calculate validation results for all targets
    const newValidationResults = targets.reduce<Record<string, StageValidationResult>>(
      (acc, target) => {
        if (!target.id) return acc;
        acc[target.id] = isValidStageTransition(
          target.stage_change_logs || [],
          selectedStage,
          goalType,
          selectedDate,
        );

        return acc;
      },
      {},
    );

    setValidationResults(newValidationResults);
  }, [targets, selectedStage, goalType, selectedDate]);

  const handleTargetSelection = (target: ITreatmentPlanGoalTarget, checked: boolean) => {
    if (checked) {
      setSelectedTargets([...selectedTargets, target]);
    } else {
      setSelectedTargets(selectedTargets.filter((t) => t.id !== target.id));
    }
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, reason?: string) => {
    if (reason && reason === 'backdropClick') return;
    setSelectedTargets([]); // Reset selections
    onClose(event, reason);
  };

  const headerConfig: ITableHeaderConfig[] = [
    {
      label: 'Target',
      sx: { flex: 1 },
      labelAlign: 'left',
      labelColor: FORTA_WHITE,
    },
    {
      label: 'Current',
      sx: { width: 180 },
      labelAlign: 'center',
      labelColor: FORTA_WHITE,
    },
    {
      label: 'Change to',
      sx: { width: 200 },
      labelAlign: 'center',
      labelColor: FORTA_WHITE,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="stage-change-confirmation"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '80%',
          maxWidth: 900,
          height: '75%',
          maxHeight: 600,
          p: 3,
          bgcolor: 'white',
          borderRadius: 2,
          boxShadow: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Typography variant="h6" fontWeight={600} mb={3}>
          The stage change will affect the following targets
        </Typography>

        <Box sx={{ flex: 1, overflow: 'hidden', mb: 3 }}>
          <FHTableContainer
            headerConfig={headerConfig}
            headerStyle={{
              height: 'auto',
              width: '100%',
              backgroundColor: FORTA_BLUE.primary,
              color: FORTA_WHITE,
              borderRadius: 2,
              mb: 2,
              marginLeft: 0,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              p: '10px 16px',
            }}
            sx={{
              backgroundColor: 'transparent',
              borderRadius: 1,
              overflow: 'auto',
            }}
            contentStyle={{
              borderRadius: 0,
              overflow: 'auto',
              height: '380px',
              pb: 4,
            }}
          >
            <Box
              sx={{
                overflow: 'auto',
                height: '100%',
                pr: 1,
                pt: 0.5,
                '&::-webkit-scrollbar-track': {
                  backgroundColor: FORTA_WHITE,
                  boxShadow: 'inset 0 0 6px rgba(0,0,0,0.2)',
                },
              }}
            >
              {targets.length > 0 ? (
                targets.map((target, index) => {
                  const validation = validationResults[target.id ?? ''] || { isValid: false };
                  return (
                    <Box
                      key={target.id}
                      sx={{
                        display: 'flex',
                        p: 2,
                        border: `1px solid ${FORTA_GRAY2}`,
                        bgcolor: selectedTargets.includes(target)
                          ? 'rgba(232, 240, 245, 1)'
                          : 'rgba(250, 250, 250, 1)',
                        color: !validation.isValid ? FORTA_GRAY4 : FORTA_BLUE.primary,
                        alignItems: 'center',
                        borderRadius: 2,
                        mb: 1,
                        opacity: !validation.isValid ? 0.8 : 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <FCheckbox
                          sx={{
                            boxShadow: 'none',
                            '& .Mui-disabled': {
                              opacity: 0.8,
                              cursor: 'not-allowed',
                            },
                          }}
                          data-testid={`${target.label}_checkbox_${validation.isValid}`}
                          disabled={!validation.isValid}
                          checked={selectedTargets.some((t) => t.id === target.id)}
                          onChange={(e) => handleTargetSelection(target, e.target.checked)}
                        />
                        <Typography
                          fontWeight={600}
                          fontSize={18}
                          sx={{ opacity: !validation.isValid ? 0.7 : 1 }}
                        >
                          {`${index + 1}: ${target.label}`}
                        </Typography>
                        {!validation.isValid && validation.reason && (
                          <FHTooltip
                            arrow
                            title={validation.reason}
                            placement="right-end"
                            tooltipBorderRadius="8px"
                            tooltipBoxShadow="0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
                            sx={{
                              '& .MuiTooltip-tooltip': {
                                padding: '12px 16px',
                              },
                            }}
                          >
                            <ErrorOutline
                              sx={{
                                fontSize: 24,
                                color: FORTA_ORANGE,
                              }}
                            />
                          </FHTooltip>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: 220,
                          position: 'relative',
                        }}
                      >
                        <Typography
                          textAlign="center"
                          fontWeight={600}
                          fontSize={18}
                          sx={{ width: '100%', opacity: !validation.isValid ? 0.7 : 1 }}
                        >
                          {target.stage_change_logs?.[target.stage_change_logs.length - 1]
                            ?.new_stage || 'N/A'}
                        </Typography>
                        {validation.isValid ? (
                          <PlayArrow
                            sx={{
                              fontSize: 28,
                              color: FORTA_SUCCESS,
                              position: 'absolute',
                              right: 0,
                            }}
                          />
                        ) : (
                          <CloseRounded
                            sx={{
                              fontSize: 28,
                              color: FORTA_ERROR,
                              position: 'absolute',
                              right: 0,
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 1,
                          width: 150,
                        }}
                      >
                        <Typography
                          textAlign="center"
                          fontWeight={600}
                          fontSize={18}
                          sx={{ opacity: !validation.isValid ? 0.7 : 1 }}
                        >
                          {selectedStage}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Typography
                  textAlign="center"
                  fontWeight={600}
                  fontSize={18}
                  mt={2}
                  color={FORTA_GRAY4}
                >
                  No targets available for this goal
                </Typography>
              )}
            </Box>
          </FHTableContainer>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            pt: 2,
            borderTop: '1px solid #E5E7EB',
            position: 'absolute',
            bottom: 24,
            left: 24,
            right: 24,
            bgcolor: 'white',
          }}
        >
          <FHButton
            variant="outlined"
            secondary
            startIcon={<CancelOutlined />}
            onClick={handleClose}
          >
            CANCEL
          </FHButton>
          <FHButton
            variant="contained"
            startIcon={<CheckCircleOutline />}
            disabled={selectedTargets.length === 0}
            data-testid="bulkChangeApplyButton"
            onClick={() => {
              onApply(
                selectedTargets.filter(
                  (target) => validationResults[target.id ?? '']?.isValid ?? false,
                ),
              );
            }}
          >
            APPLY CHANGES
          </FHButton>
        </Box>
      </Box>
    </Modal>
  );
}

export default StageChangeConfirmation;
